import chroma from "chroma-js";
import classNames from "classnames";
import { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import { Appearance } from "common/generator/components/button-appearance";
import type { WireTab } from "common/wire";
import { UIBackgroundStyle } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { useLayer } from "react-laag";
import { useResizeDetector } from "react-resize-detector";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { useColorFromSeed } from "sharedUI/ui/views/user-preview-icon/user-preview-icon";
import tw from "twin.macro";
import { APP_MODAL_ROOT } from "webapp/lib/constants";
import { css, styled } from "webapp/styled-components";
import Img from "webapp/ui/smart/img/img";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { WireContainer } from "webapp/ui/views/wire-container/wire-container";
import { useResponsiveSizeClass } from "webapp/wire-renderers/responsive-provider";

export interface DesktopNavBarProps {
    readonly tabs: readonly WireTab[];
    readonly selected?: WireTab;
    readonly onSelectionChange?: (tab: WireTab) => void;
    readonly title: string;
    readonly isUserProfileActive: boolean;
    readonly logo?: string;
    readonly className?: string;
    readonly signedInUserEmail?: string;
    readonly signedInUserName?: string;
    readonly onUserButtonPressed?: () => void;
    readonly onSignInPressed?: () => void;
    readonly onSignOutPressed?: () => void;
    readonly onSignUpPressed?: () => void;
    readonly onTitlePressed?: () => void;
}

const PopupMenuStyle = styled.div`
    &.open {
        ${tw`translate-y-0 opacity-100 pointer-events-auto`};
    }

    &.close {
        ${tw`-translate-y-2 opacity-0 pointer-events-none`};
    }
`;

interface UserProfileMenuProps {
    layerProps: any;
    onClose: () => void;
    onUserButtonPressed: () => void;
    onSignOutPressed?: () => void;
}
export const UserProfileMenu: GlideFC<UserProfileMenuProps> = p => {
    return (
        <div {...p.layerProps} tw="rounded-lg shadow-md width[192px] text-text-dark text-base bg-bg-front py-2">
            <ul
                onClick={p.onClose}
                tw="all-child:(px-3 py-2 text-sm cursor-pointer hover:bg-bg-back not-last:(border-b border-n200))">
                <li onClick={p.onUserButtonPressed}>View Profile</li>
                {p.onSignOutPressed && (
                    <li onClick={p.onSignOutPressed}>{getLocalizedString("signOut", AppKind.Page)}</li>
                )}
            </ul>
        </div>
    );
};

export const DesktopNavBar: React.VFC<DesktopNavBarProps> = p => {
    const {
        title,
        tabs,
        className,
        selected,
        onSelectionChange,
        onSignUpPressed,
        onUserButtonPressed,
        onSignInPressed,
        signedInUserEmail,
        signedInUserName,
        onSignOutPressed,
        onTitlePressed,
        isUserProfileActive,
        logo,
    } = p;

    const [showMenu, setShowMenu] = React.useState(false);

    const username = signedInUserName ?? signedInUserEmail ?? "?";

    const colorPreview = useColorFromSeed(username);
    const background = chroma(colorPreview).brighten(3).css();
    const theme = useWireAppTheme();

    const pageThemeClass = (theme.pageTheme ?? "accent").toLowerCase();
    const navRegion = (
        <>
            {tabs.map((t, index) => (
                <button
                    onClick={() => {
                        setShowMenu(false);
                        onSelectionChange?.(t);
                    }}
                    className={classNames(selected === t && "selected", pageThemeClass)}
                    css={css`
                        &.selected {
                            ${tw`background[rgba(0,0,0,0.1)] border-white opacity-100
                              gp-sm:(bg-transparent border-b-2 pt-0.5)`}
                        }

                        &.highlight {
                            ${tw`border-accent`}
                        }
                    `}
                    tw="px-4 py-2 rounded-xl border-transparent h-full transition duration-200 cursor-pointer
                        whitespace-nowrap bg-n0A opacity-80 hover:(opacity-100) focus-visible:(ring-2 ring-offset-1
                        ring-n0A) text-left text-base
                        gp-xl:mr-8
                        gp-sm:(rounded-none mr-6 p-0 text-center)"
                    key={index}>
                    {t.title}
                </button>
            ))}
        </>
    );

    const sizeClass = useResponsiveSizeClass();

    const { width: containerWidth, ref: containerRef } = useResizeDetector();
    const { width: buttonsWidth, ref: buttonsRef } = useResizeDetector();

    let containerTheme = UIBackgroundStyle.Accent;
    if (theme.pageTheme === "Highlight") {
        containerTheme = UIBackgroundStyle.Highlight;
    } else if (theme.pageTheme === "Dark") {
        containerTheme = UIBackgroundStyle.Dark;
    }

    const [showUserMenu, setShowUserMenu] = React.useState(false);

    const { layerProps, triggerProps, renderLayer } = useLayer({
        isOpen: showUserMenu,
        auto: true,
        triggerOffset: 4,
        placement: "bottom-end",
        onOutsideClick: () => setShowUserMenu(false),
        container: APP_MODAL_ROOT,
    });

    const userButton =
        onUserButtonPressed === undefined ? undefined : (
            <>
                <button
                    {...triggerProps}
                    onClick={() => setShowUserMenu(cv => !cv)}
                    className={classNames(isUserProfileActive && "force-outline")}
                    style={{
                        color: colorPreview,
                        backgroundColor: background,
                    }}
                    css={css`
                        &.force-outline {
                            ${tw`after:ring-2 ring-white`}
                        }
                    `}
                    tw="flex relative capitalize justify-center items-center text-base w-6 h-6 flex-shrink-0
                        cursor-pointer rounded-full mr-1 shadow-none transform transition after:(content absolute
                        -inset-0.5 rounded-full pointer-events-none transition-shadow) focus-visible:after:(ring-2
                        ring-white) hover:(shadow-lg-soft scale-105)
                        gp-sm:(w-7 h-7)">
                    {username[0]}
                </button>
                {showUserMenu &&
                    renderLayer(
                        <UserProfileMenu
                            layerProps={layerProps}
                            onClose={() => setShowUserMenu(false)}
                            onUserButtonPressed={onUserButtonPressed}
                            onSignOutPressed={onSignOutPressed}
                        />
                    )}
            </>
        );

    if (sizeClass === undefined) {
        return (
            <>
                <WireContainer background={containerTheme} tw="box-shadow[0 0 0 1px rgb(0 0 0 / 8%)]">
                    <header
                        ref={containerRef}
                        className={className}
                        tw="h-14 flex flex-col text-text-contextual-dark w-full z-20">
                        <div tw="flex justify-between">
                            <button tw="p-2 -ml-2" onClick={() => setShowMenu(cv => !cv)}>
                                {!showMenu && <GlideIcon kind="monotone" icon="mt-menu" iconSize={20} />}
                                {showMenu && <GlideIcon kind="stroke" icon="st-close" iconSize={20} />}
                            </button>
                            <div
                                onClick={onTitlePressed}
                                className={classNames(onTitlePressed !== undefined ? "active" : "")}
                                css={css`
                                    &.active {
                                        ${tw`cursor-pointer`}
                                    }
                                `}
                                tw="h-14 flex flex-shrink items-center font-extrabold text-xl mr-2 truncate min-w-0">
                                {logo !== undefined && <Img src={logo} tw="w-8 h-8 mr-2 object-contain" />}
                                {title}
                            </div>
                            <div ref={buttonsRef} tw="flex-shrink-0 self-center flex justify-center items-center">
                                {onSignInPressed !== undefined && (
                                    <WireButton
                                        onClick={onSignInPressed}
                                        appearance={Appearance.Bordered}
                                        tw="flex-shrink-0 not-last:mr-1">
                                        Sign In
                                    </WireButton>
                                )}
                                {onSignUpPressed !== undefined && (
                                    <WireButton
                                        onClick={onSignUpPressed}
                                        appearance={Appearance.Filled}
                                        tw="flex-shrink-0 not-last:mr-1">
                                        Sign Up
                                    </WireButton>
                                )}
                                {onUserButtonPressed !== undefined && userButton}
                            </div>
                        </div>
                    </header>
                    <PopupMenuStyle
                        className={showMenu ? "open" : "close"}
                        tw="absolute top-full left-0 right-0 transform transition-all">
                        <WireContainer background={containerTheme} tw="mt-0! box-shadow[inset 0 1px #ffffff33]">
                            <nav className="scrollbar-hide" tw="flex flex-col py-2 items-stretch text-white">
                                {navRegion}
                                <div tw="w-10 flex-shrink" />
                            </nav>
                        </WireContainer>
                        <div tw="shadow-2xl-dark h-5 -mt-5 pointer-events-none" />
                    </PopupMenuStyle>
                </WireContainer>
            </>
        );
    }

    return (
        <WireContainer background={containerTheme} tw="box-shadow[0 0 0 1px rgb(0 0 0 / 8%)]">
            <header
                ref={containerRef}
                className={className}
                tw="overflow-hidden flex text-text-contextual-dark w-full relative">
                <div tw="flex flex-wrap flex-shrink min-w-0">
                    <div
                        onClick={onTitlePressed}
                        className={classNames(onTitlePressed !== undefined ? "active" : "")}
                        style={{
                            maxWidth: (containerWidth ?? 10000) - ((buttonsWidth ?? 0) + 12),
                        }}
                        css={css`
                            &.active {
                                ${tw`cursor-pointer`}
                            }
                        `}
                        tw="h-14 flex items-center flex-shrink-0 font-extrabold text-xl mr-6 truncate min-w-0">
                        {logo !== undefined && <Img src={logo} tw="w-8 h-8 mr-2 object-contain" />}
                        {title}
                    </div>
                    <nav
                        className="scrollbar-hide"
                        tw="h-14 flex px-0.5 flex-shrink min-w-0 overflow-x-auto items-center">
                        {navRegion}
                        <div style={{ width: buttonsWidth }} tw="flex-shrink" />
                    </nav>
                </div>
                <div
                    ref={buttonsRef}
                    id="nav-bar-button-region"
                    tw="flex-shrink-0 self-center absolute right-1 top-0 h-14 flex justify-center items-center">
                    {onSignInPressed !== undefined && (
                        <WireButton
                            onClick={onSignInPressed}
                            appearance={Appearance.Bordered}
                            tw="flex-shrink-0 not-last:mr-1">
                            Sign In
                        </WireButton>
                    )}
                    {onSignUpPressed !== undefined && (
                        <WireButton
                            onClick={onSignUpPressed}
                            appearance={Appearance.Filled}
                            tw="flex-shrink-0 not-last:mr-1">
                            Sign Up
                        </WireButton>
                    )}
                    {onUserButtonPressed !== undefined && userButton}
                </div>
            </header>
        </WireContainer>
    );
};
